import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react'
import { trackMapSearch } from '../lib/analytics'
import type { BookIndex, WorldData } from './availability'
import { buildRegionGroups } from './availability'
import BookTile from './BookTile'

// Third tab of the availability map: a vertical, scrollable, searchable list of
// every place the sport is available, grouped under region headers with USA at
// the top. It is the text equivalent of both maps — and the only surface that
// can show the five places Natural Earth's 110m geometry has no shape for
// (Singapore, Samoa, French Polynesia, French Guiana, U.S. Minor Outlying
// Islands), which is why those rows carry a "list only" marker.

interface Props {
  index: BookIndex[]
  bettingStates: Set<string>
  dfsStates: Set<string>
  predictionStates: Set<string>
  usStates: { id: string, name: string }[]
  world: WorldData
  sport: string
  apiBase: string
  labels: { none: string }
  /** Sportsbook mode: tiles are league logos — no brand background. */
  plain?: boolean
}

export default function ListView({ index, bettingStates, dfsStates, predictionStates, usStates, world, sport, apiBase, labels, plain = false }: Props) {
  const [query, setQuery] = useState('')
  // Keeps typing responsive: the grouped rebuild (up to ~230 rows, each
  // resolving every carrier) runs against a lagging value while the input
  // itself stays on the urgent render path.
  const deferredQuery = useDeferredValue(query)

  const groups = useMemo(
    () => buildRegionGroups(index, bettingStates, dfsStates, predictionStates, usStates, deferredQuery, world),
    [index, bettingStates, dfsStates, predictionStates, usStates, deferredQuery, world],
  )

  const total = groups.reduce((a, g) => a + g.rows.length, 0)
  const searching = deferredQuery.trim().length > 0

  // Report the search once the term has sat unchanged for a second — long
  // enough that mid-word prefixes mostly don't fire — deduped so retyping the
  // same term isn't a second event. Lowercased to keep the GA4 dimension from
  // splitting "Brazil" and "brazil" into separate rows; typos stay visible,
  // which is the point ("El Salavdor" in the data is a finding, not noise).
  const lastTracked = useRef('')
  useEffect(() => {
    const term = deferredQuery.trim().toLowerCase()
    if (!term || term === lastTracked.current) return
    const timer = setTimeout(() => {
      lastTracked.current = term
      trackMapSearch(sport, term, total)
    }, 1000)
    return () => clearTimeout(timer)
  }, [deferredQuery, sport, total])

  return (
    <div className="am-list">
      <div className="am-list-search">
        <input
          type="search"
          className="am-list-input"
          placeholder="Search states and countries…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          aria-label="Search states and countries"
        />
        {query && (
          <button type="button" className="am-list-clear" onClick={() => setQuery('')} aria-label="Clear search">✕</button>
        )}
      </div>

      <div className="am-list-scroll">
        {total === 0 && (
          <p className="am-list-empty">No states or countries match “{deferredQuery}”.</p>
        )}

        {groups.map(g => (
          <section key={g.region} className="am-list-group">
            <h3 className="am-list-head">
              {g.label}
              <span className="am-list-count">{g.rows.length}</span>
            </h3>
            <ul className="am-list-rows">
              {g.rows.map(row => (
                <li key={`${g.region}-${row.id}`} className={`am-list-row am-row-${row.status}`}>
                  <span className="am-list-dot" aria-hidden="true" />
                  <span className="am-list-name">
                    {row.name}
                    {row.qualifiers.map(qf => (
                      <span key={qf.book} className="am-list-note">{qf.book}: {qf.note}</span>
                    ))}
                    {!row.mapped && <span className="am-list-note">list only — no map shape</span>}
                  </span>
                  <span className="am-list-books">
                    {row.books.sportsbooks.length === 0 && row.books.dfs.length === 0 && row.books.prediction.length === 0
                      ? <span className="am-list-na">{labels.none}</span>
                      // Sportsbooks, then DFS, then prediction markets, each run
                      // divided so a prediction market is never read as a book.
                      : ([
                          row.books.sportsbooks,
                          row.books.dfs,
                          row.books.prediction,
                        ] as const)
                          .filter(group => group.length > 0)
                          .map((group, gi) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <span className="am-list-run" key={gi}>
                              {gi > 0 && <span className="am-list-sep" aria-hidden="true" />}
                              {group.map(b => (
                                <BookTile
                                  key={b.name}
                                  book={b}
                                  apiBase={apiBase}
                                  sport={sport}
                                  place={row.id}
                                  scope={g.region === 'usa' ? 'state' : 'country'}
                                  size="sm"
                                  plain={plain}
                                />
                              ))}
                            </span>
                          ))}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))}

        {!searching && (
          <p className="am-list-foot">
            Showing places with live coverage. Search to check anywhere else.
          </p>
        )}
      </div>
    </div>
  )
}
